import { useEffect, useState } from 'react'
import {
  discardWorkflow,
  fetchWorkflow,
  startWorkflowSchedule,
} from '@/services/workflows/workflowScheduleApi'

/**
 * Drive one scheduled-task card.
 *
 * The same trap `useProposalDecision` documents applies here: a card rehydrated
 * from message history would offer Start forever, so the row is resolved from
 * the server on mount. A schedule started last week must not look startable
 * today, and a discarded one must not offer to start at all.
 * The argument is the parsed `tool_result` payload, so its keys are the
 * contract's snake_case. Reading `workflowId` left the id undefined, sending
 * every Start to `/v1/workflows/undefined` — a 404 the card printed verbatim.
 */
export function useScheduleStart({ workflow_id: workflowId, cron, timezone }) {
  const [status, setStatus] = useState('draft')
  const [error, setError] = useState(null)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (!workflowId) return undefined
    const controller = new AbortController()
    fetchWorkflow({ workflowId, signal: controller.signal })
      .then((row) => setStatus(row.scheduleEnabled ? 'started' : 'draft'))
      // A card that cannot reach the server stays actionable: Start would fail
      // loudly, which beats hiding the only control that arms it.
      .catch(() => {})
    return () => controller.abort()
  }, [workflowId])

  const act = async (action) => {
    if (pending || status !== 'draft') return
    setPending(true)
    setError(null)
    try {
      if (action === 'start') {
        await startWorkflowSchedule({ workflowId, cron, timezone })
        setStatus('started')
      } else {
        await discardWorkflow({ workflowId })
        setStatus('discarded')
      }
    } catch (caught) {
      setError(caught?.message || 'Could not update that scheduled task')
    } finally {
      setPending(false)
    }
  }

  return { status, error, pending, act }
}
